import React, { useEffect, useState, useRef } from 'react';
import { Box, Grid, Paper, Typography, TextField, Button, Dialog, DialogContent, Table, TableHead, TableRow, TableCell, TableBody, TableFooter, Snackbar, Alert, FormControl, InputLabel, Select, MenuItem, Divider } from '@mui/material';
import axios from 'axios';
import { useAuth } from '../../../context/AuthContext';
import TransportMaster from './TransportMaster';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { toProperCase } from '../../../utils/formatRows';

const formatDate = (date) => {
    if (!date) return '';
    if (dayjs.isDayjs(date)) return date.format('DD/MM/YYYY');
    const d = new Date(date);
    return isNaN(d) ? '' : d.toISOString().split('T')[0];
};

const parseDate = (dateStr) => {
    if (!dateStr || typeof dateStr !== 'string') return null;
    // Try parsing as DD/MM/YYYY first (expected format from backend)
    let parsed = dayjs(dateStr, 'DD/MM/YYYY', true);
    if (parsed.isValid()) return parsed;
    // Fallback to other common formats
    parsed = dayjs(dateStr, ['YYYY-MM-DD', 'MM/DD/YYYY', 'YYYY-MM-DDTHH:mm:ss.SSSZ'], true);
    if (parsed.isValid()) return parsed;
    return null;
};

const TransporterDetails = ({ initialData = {},
    //  onNext, 
    onBack, connectionString, onChange }) => {
    const API = connectionString;
    const { user, selectedCompany, loading } = useAuth();
    const companyId = selectedCompany?.CompId || null;
    const [transporterName, setTransporterName] = useState(initialData?.transporterName || '');
    const [transportId, setTransportId] = useState(initialData?.transportId || null);
    const [gstNo, setGstNo] = useState(initialData?.gstNo || '');
    const [vehicleNumber, setVehicleNumber] = useState(initialData?.vehicleNumber || '');
    const [dispatchFrom, setDispatchFrom] = useState(initialData?.dispatchFrom || '');
    const [dispatchTo, setDispatchTo] = useState(initialData?.dispatchTo || '');
    const [docThrough, setDocThrough] = useState(initialData?.docThrough || '');
    const [lrNumber, setLrNumber] = useState(initialData?.lrNumber || '');
    const [lrDate, setLrDate] = useState(parseDate(initialData?.lrDate) || null);
    const [totalPkg, setTotalPkg] = useState(initialData?.totalPkg || '');
    const [ewayBillNumber, setEwayBillNumber] = useState(initialData?.ewayBillNumber || '');
    const [ewayBillDate, setEwayBillDate] = useState(formatDate(initialData?.ewayBillDate) || '');
    const [irnNumber, setIrnNumber] = useState(initialData?.irnNumber || '');
    const [irnDate, setIrnDate] = useState(formatDate(initialData?.irnDate) || '');
    const [ackNumber, setAckNumber] = useState(initialData?.ackNumber || '');
    const [ackDate, setAckDate] = useState(formatDate(initialData?.ackDate) || '');
    const [suggestions, setSuggestions] = useState([]);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const [error, setError] = useState('');
    const dropdownRef = useRef(null);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success"
    });

    const [transMode, setTransMode] = useState(initialData?.transMode || "Road");
    const [vehicleType, setVehicleType] = useState(initialData?.vehicleType || "Regular");
    const [subType, setSubType] = useState(initialData?.subType || "Supply");

    const requiredAsteriskStyle = {
        '& .MuiFormLabel-asterisk': {
            color: 'red',
        },
    };

       useEffect(() => {
        setTransMode(initialData?.transMode || "Road");
        setVehicleType(initialData?.vehicleType || "Regular");
        setSubType(initialData?.subType || "Supply");
    }, [initialData]);

    // Debug initialData.lrDate to verify what's being received
    useEffect(() => {
        console.log('initialData.lrDate:', initialData?.lrDate);
        console.log('Parsed lrDate:', parseDate(initialData?.lrDate));
    }, [initialData?.lrDate]);

    // Function to fetch transporter suggestions
    const fetchTransporterSuggestions = async (name) => {
        try {
            const res = await axios.get(`${API}/api/transport/search`, {
                params: {
                    mode: 'search',
                    TransporterName: name,
                    companyId,
                },
            });
            setSuggestions(res.data || []);
        } catch (error) {
            setSuggestions([]);
        }
    };

    // useEffect to call the function when conditions are met
    useEffect(() => {
        if (transporterName && dropdownOpen) {
            fetchTransporterSuggestions(transporterName);
        }
    }, [transporterName, dropdownOpen]);

    // Close dropdown if clicked outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelectTransporter = (transporter) => {
        setTransporterName(transporter.TransporterName);
        setTransportId(transporter.TransPortID);
        setGstNo(transporter.GSTNO);
        setDropdownOpen(false);
        setSuggestions([]);
    };

    // const handleNext = () => {
    //     if (!transporterName) {
    //         setError('Transporter Name is required.');
    //         return;
    //     }
    //     setError('');
    //     onNext({
    //         transportId,
    //         transporterName,
    //         gstNo,
    //         vehicleNumber,
    //         dispatchFrom,
    //         dispatchTo,
    //         docThrough,
    //         lrNumber,
    //         lrDate: lrDate ? lrDate.format('DD/MM/YYYY') : '',
    //         totalPkg,
    //         ewayBillNumber,
    //         ewayBillDate,
    //         irnNumber,
    //         irnDate,
    //         ackNumber,
    //         ackDate,
    //     });
    // };

    useEffect(() => {
        onChange({
            transportId,
            transporterName,
            gstNo,
            vehicleNumber,
            dispatchFrom,
            dispatchTo,
            docThrough,
            lrNumber,
            lrDate: lrDate ? lrDate.format('DD/MM/YYYY') : '',
            totalPkg,
            ewayBillNumber,
            ewayBillDate,
            irnNumber,
            irnDate,
            ackNumber,
            ackDate,
            transMode,
            vehicleType,
            subType
        });
    }, [ transporterName, vehicleNumber, totalPkg, lrNumber, lrDate, transMode, vehicleType, subType ]);

    const handlePopupClose = (data) => {
        setShowPopup(false)

        if (data?.success) {
            setSnackbar({ open: true, message: data.message, severity: "success" });
        }
    };

    return (
        <Paper elevation={2} sx={{ p: 3, position: 'relative', mt: -1.5 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
                <Box
                    display="flex"
                    flexDirection={{ xs: 'column', md: 'row' }}
                >
                    {/* Left Column */}
                    <Box
                        flex={{ xs: '1 1 100%', md: '0 0 59%' }}
                        sx={{
                            width: { xs: '100%', md: '59%' },
                        }}
                    >
                        {/* Row-1 */}
                        <Grid container spacing={1}>

                            <Grid item size={{ xs: 12, md: 6 }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    // required
                                    label="Transporter Name"
                                    value={toProperCase(transporterName)}
                                    onClick={() => setDropdownOpen(true)}
                                    onChange={(e) => {
                                        setTransporterName(toProperCase(e.target.value));
                                        setDropdownOpen(true);
                                    }}
                                    InputLabelProps={{ sx: requiredAsteriskStyle }}
                                    sx={{
                                        '& .MuiInputBase-root': {
                                            height: 38,
                                        }
                                    }
                                    }
                                />
                                {/* {dropdownOpen && transporterName && (
                                    <Paper
                                        ref={dropdownRef}
                                        sx={{
                                            position: 'absolute',
                                            zIndex: 10,
                                            maxHeight: 250,
                                            overflowY: 'auto',
                                            mt: 1,
                                            width: '100%',
                                        }}
                                    >
                                        {suggestions.length > 0 ? (
                                            suggestions.map((item) => (
                                                <Box
                                                    key={item.id}
                                                    sx={{
                                                        px: 2,
                                                        py: 1,
                                                        cursor: 'pointer',
                                                        '&:hover': { backgroundColor: '#eee' },
                                                    }}
                                                    onClick={() => handleSelectTransporter(item)}
                                                >
                                                    <strong>{item.TransporterName}</strong> — {item.GSTNO}
                                                </Box>
                                            ))
                                        ) : (
                                            <Box sx={{ px: 2, py: 1 }}>No results found</Box>
                                        )}
                                        <Box sx={{ textAlign: 'right', p: 1 }}>
                                            <Button size="small" onClick={() => setShowPopup(true)}>
                                                + Add New
                                            </Button>
                                        </Box>
                                    </Paper>
                                )} */}
                                {dropdownOpen && transporterName && (
                                    <Paper
                                        ref={dropdownRef}
                                        elevation={0}
                                        sx={{
                                            mt: 1,
                                            position: 'absolute',
                                            width: {  xs: '90%' , sm:"50%"},
                                            zIndex: 10,
                                            border: '1px solid rgba(0,0,0,0.2)',
                                            borderRadius: 1,
                                            maxHeight: 240,
                                            overflowY: 'auto',
                                            backgroundColor: '#fff',
                                        }}
                                    >
                                        <Table size="small" stickyHeader>
                                            {/* ================= HEADER ================= */}
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell
                                                        sx={{
                                                            backgroundColor: '#E0E0E0',
                                                            fontWeight: 600,
                                                            fontSize: '0.75rem',
                                                            position: 'sticky',
                                                            top: 0,
                                                            zIndex: 1,
                                                        }}
                                                    >
                                                        Transporter Name
                                                    </TableCell>

                                                    <TableCell
                                                        sx={{
                                                            backgroundColor: '#E0E0E0',
                                                            fontWeight: 600,
                                                            fontSize: '0.75rem',
                                                            position: 'sticky',
                                                            top: 0,
                                                            zIndex: 1,
                                                            //   width:'140px'
                                                        }}
                                                    >
                                                        GST No
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>

                                            {/* ================= BODY ================= */}
                                            <TableBody>
                                                {suggestions.length > 0 ? (
                                                    suggestions.map((item, i) => (
                                                        <TableRow
                                                            key={i}
                                                            hover
                                                            sx={{ cursor: 'pointer' }}
                                                            onMouseDown={() => {
                                                                handleSelectTransporter(item);
                                                                setDropdownOpen(false);
                                                            }}
                                                        >
                                                            <TableCell sx={{ fontSize: '0.8rem', fontWeight: 500, wordWrap: 'normal', wordBreak: 'break-word' }}>
                                                                {item.TransporterName}
                                                            </TableCell>

                                                            <TableCell sx={{ fontSize: '0.8rem', wordWrap: 'normal', wordBreak: 'break-word' }}>
                                                                {item.GSTNO || '-'}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))
                                                ) : (
                                                    <TableRow>
                                                        <TableCell colSpan={2} align="center" sx={{ color: 'error.main' }}>
                                                            Transporter not found
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>

                                            {/* ================= FOOTER ================= */}
                                            <TableFooter
                                                sx={{
                                                    position: 'sticky',
                                                    bottom: 0,
                                                    backgroundColor: '#fff',
                                                }}
                                            >
                                                <TableRow hover onMouseDown={() => setShowPopup(true)}>
                                                    <TableCell
                                                        colSpan={2}
                                                        align="center"
                                                        sx={{
                                                            color: 'primary.main',
                                                            fontWeight: 600,
                                                            cursor: 'pointer',
                                                        }}
                                                    >
                                                        ➕ Add New Transporter
                                                    </TableCell>
                                                </TableRow>
                                            </TableFooter>
                                        </Table>
                                    </Paper>
                                )}

                            </Grid>
                            <Grid item size={{ xs: 12, md: 3 }}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Trans Mode</InputLabel>
                                    <Select
                                        value={transMode}
                                        label="Trans Mode"
                                        onChange={(e) => setTransMode(e.target.value)}
                                        sx={{ height: 38 }}
                                    >
                                        <MenuItem value="Road">Road</MenuItem>
                                        <MenuItem value="Rail">Rail</MenuItem>
                                        <MenuItem value="Air">Air</MenuItem>
                                        <MenuItem value="Ship / Road cum Ship">Ship / Road cum Ship</MenuItem>
                                    </Select>
                                </FormControl>

                            </Grid>

                            <Grid item size={{ xs: 12, md: 3 }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="GST No."
                                    value={gstNo}
                                    disabled
                                    sx={{
                                        '& .MuiInputBase-root': {
                                            height: 38,
                                        }
                                    }
                                    }
                                />
                            </Grid>
                        </Grid>

                        {/* Row-2 */}
                        <Grid container spacing={1} py={2}>
                       
                            <Grid item size={{ xs: 12, sm: 6, md:2.7}}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Vehicle No."
                                    value={vehicleNumber}
                                    onChange={(e) => setVehicleNumber(e.target.value)}
                                    onBlur={() => {
                                        const cleaned = vehicleNumber.replace(/[\s\-\/\.!@#$%^&*()_+=\[\]{};:'",<>?\\|`~]/g, '');
                                        setVehicleNumber(cleaned.toUpperCase());
                                    }}
                                    sx={{
                                        '& .MuiInputBase-root': {
                                            height: 38,
                                        },
                                    }}
                                />
                            </Grid>

                            <Grid item size={{ xs: 12, sm: 6, md: 1.8}}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Vehicle Type</InputLabel>
                                    <Select
                                        value={vehicleType}
                                        label="Vehicle Type"
                                        onChange={(e) => setVehicleType(e.target.value)}
                                        sx={{ height: 38 }}
                                    >
                                        <MenuItem value="Regular">Regular</MenuItem>
                                        <MenuItem value="ODC">ODC</MenuItem>
                                    </Select>
                                </FormControl>

                            </Grid>

                            <Grid item size={{ xs: 12, sm: 6, md: 1.5}}>                                
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Total Pkg."
                                    type="number"
                                    value={totalPkg}
                                    onChange={(e) => setTotalPkg(e.target.value)}
                                    sx={{
                                        '& .MuiInputBase-root': {
                                            height: 38,
                                        },
                                    }}
                                />
                            </Grid>

                            <Grid item size={{ xs: 12, sm: 6, md: 3}}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Sub Type</InputLabel>
                                    <Select
                                        value={subType}
                                        label="Sub Type"
                                        onChange={(e) => setSubType(e.target.value)}
                                        sx={{ height: 38 }}
                                    >
                                        <MenuItem value="Supply">Supply</MenuItem>
                                        <MenuItem value="Import">Import</MenuItem>
                                        <MenuItem value="Export">Export</MenuItem>
                                        <MenuItem value="Job Work">Job Work</MenuItem>
                                        <MenuItem value="For Own Use">For Own Use</MenuItem>
                                        <MenuItem value="Job Work Returns">Job Work Returns</MenuItem>
                                        <MenuItem value="Sales Return">Sales Return</MenuItem>
                                        <MenuItem value="Others">Others</MenuItem>
                                        <MenuItem value="SKD / CKD / Lots">SKD / CKD / Lots</MenuItem>
                                        <MenuItem value="Line Sales">Line Sales</MenuItem>
                                        <MenuItem value="Recipient Not Known">Recipient Not Known</MenuItem>
                                        <MenuItem value="Exhibition / Fairs">Exhibition / Fairs</MenuItem>

                                    </Select>
                                </FormControl>

                            </Grid>
        
                            <Grid item size={{ xs: 12,sm:12, md: 3 }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Doc Through"
                                    value={toProperCase(docThrough)}
                                    onChange={(e) => setDocThrough(toProperCase(e.target.value))}
                                    sx={{
                                        '& .MuiInputBase-root': {
                                            height: 38,
                                        },
                                    }}
                                />
                            </Grid>

                        </Grid>

                        {/* Row-3 */}
                        <Box display="flex" flexWrap="wrap" gap={1}>
                            {/* Disp. From */}
                            <Box
                                sx={{
                                    width: {
                                        xs: '100%',
                                        sm: 'calc(50% - 4px)',
                                        md: 'calc(25% - 6px)',
                                    },
                                }}
                            >
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Disp. From"
                                    value={dispatchFrom}
                                    onChange={(e) => setDispatchFrom(e.target.value)}
                                    sx={{
                                        '& .MuiInputBase-root': {
                                            height: 38,
                                        },
                                    }}
                                />
                            </Box>

                            <Box
                                sx={{
                                    width: {
                                        xs: '100%',
                                        sm: 'calc(50% - 4px)',
                                        md: 'calc(25% - 6px)',
                                    },
                                }}
                            >
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Disp. To"
                                    value={dispatchTo}
                                    onChange={(e) => setDispatchTo(e.target.value)}
                                    sx={{
                                        '& .MuiInputBase-root': {
                                            height: 38,
                                        },
                                    }}
                                />
                            </Box>

                            <Box
                                sx={{
                                    width: {
                                        xs: '100%',
                                        sm: 'calc(50% - 4px)',
                                        md: 'calc(25% - 6px)',
                                    },
                                }}
                            >
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="LR No."
                                    value={lrNumber}
                                    onChange={(e) => setLrNumber(e.target.value)}
                                    sx={{
                                        '& .MuiInputBase-root': {
                                            height: 38,
                                        },
                                    }}
                                />
                            </Box>

                            <Box
                                sx={{
                                    width: {
                                        xs: '100%',
                                        sm: 'calc(50% - 4px)',
                                        md: 'calc(25% - 6px)',
                                    },
                                    mb: 2,
                                }}
                            >
                                <DatePicker
                                    label="LR Date"
                                    value={lrDate}
                                    onChange={(date) => setLrDate(date ? date : null)}
                                    format="DD/MM/YYYY"
                                    sx={{
                                        '& .MuiOutlinedInput-root': { height: 38, display: 'flex', alignItems: 'center' },
                                        '& .MuiOutlinedInput-input': { fontSize: '0.875rem', padding: '0 12px' }
                                    }}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            size: 'small',

                                        },
                                    }}
                                />
                            </Box>
                        </Box>
                    </Box>

                    {/* Desktop → Vertical */}
                    <Divider
                        orientation="vertical"
                        flexItem
                        sx={{
                            display: { xs: 'none', md: 'block' },
                            ml: 1,
                        }}
                    />

                    {/* Mobile / Tablet → Horizontal */}
                    <Divider
                        sx={{
                            display: { xs: 'block', md: 'none' },
                            mb: 1.5,
                            mt: -0.5
                        }}
                    />


                    {/* Right Column */}
                    <Box
                        flex={{ xs: '1 1 100%', md: '0 0 39%' }}
                        sx={{
                            width: { xs: '100%', md: '39%' },
                        }}
                        pl={{ md: 1 }}
                    >
                        {/* Row 1 */}
                        <Box display="flex" gap={1}>
                            <Box mb={2} flex={1} minWidth={0}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    disabled
                                    label="E-Way Bill No."
                                    value={ewayBillNumber}
                                    onChange={(e) => setEwayBillNumber(e.target.value)}
                                    sx={{
                                        '& .MuiInputBase-root': {
                                            height: 38,
                                        },
                                    }}
                                />
                            </Box>
                            <Box mb={2} flex={1} minWidth={0}>
                                <DatePicker
                                    label="E-Way Bill Date"
                                    disabled
                                    value={parseDate(ewayBillDate)}
                                    onChange={(date) =>
                                        setEwayBillDate(date ? date.format('DD/MM/YYYY') : '')
                                    }
                                    format="DD/MM/YYYY"
                                    sx={{
                                        '& .MuiOutlinedInput-root': { height: 38, display: 'flex', alignItems: 'center' },
                                        '& .MuiOutlinedInput-input': { fontSize: '0.875rem', padding: '0 12px' }
                                    }}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            size: 'small',

                                        },
                                    }}
                                />
                            </Box>
                        </Box>

                        {/* Row 2 */}
                        <Box display="flex" gap={1}>
                            <Box mb={2} flex={1} minWidth={0}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    disabled
                                    label="Ack No."
                                    value={ackNumber}
                                    onChange={(e) => setAckNumber(e.target.value)}
                                    sx={{
                                        '& .MuiInputBase-root': {
                                            height: 38,
                                        },
                                    }}
                                />
                            </Box>
                            <Box mb={2} flex={1} minWidth={0}>
                                <DatePicker
                                    label="Ack Date"
                                    disabled
                                    value={parseDate(ackDate)}
                                    onChange={(date) =>
                                        setAckDate(date ? date.format('DD/MM/YYYY') : '')
                                    }
                                    format="DD/MM/YYYY"
                                    sx={{
                                        '& .MuiOutlinedInput-root': { height: 38, display: 'flex', alignItems: 'center' },
                                        '& .MuiOutlinedInput-input': { fontSize: '0.875rem', padding: '0 12px' }
                                    }}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            size: 'small',

                                        },
                                    }}
                                />
                            </Box>
                        </Box>

                        {/* Row 3 */}
                        <Box display="flex" gap={1}>
                            <Box mb={2} flex={1} minWidth={0}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    disabled
                                    label="IRN No."
                                    value={irnNumber}
                                    onChange={(e) => setIrnNumber(e.target.value)}
                                    sx={{
                                        '& .MuiInputBase-root': {
                                            height: 38,
                                        },
                                    }}
                                />
                            </Box>
                            {/* <Box mb={2} flex={1} minWidth={0}>
                                <DatePicker
                                    label="IRN Date"
                                    disabled
                                    value={parseDate(irnDate)}
                                    onChange={(date) =>
                                        setIrnDate(date ? date.format('DD/MM/YYYY') : '')
                                    }
                                    format="DD/MM/YYYY"
                                    sx={{
                                        '& .MuiOutlinedInput-root': { height: 38, display: 'flex', alignItems: 'center' },
                                        '& .MuiOutlinedInput-input': { fontSize: '0.875rem', padding: '0 12px' }
                                    }}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            size: 'small',

                                        },
                                    }}
                                />
                            </Box> */}
                        </Box>
                    </Box>
                </Box>
            </LocalizationProvider>

            {error && (
                <Typography color="error" mt={2}>
                    {error}
                </Typography>
            )}

            {/* <Box mt={4} display="flex" justifyContent="flex-end">
               <Button variant="contained" onClick={handleNext}>
                    Next
                </Button> 
            </Box> */}

            <Dialog open={showPopup} onClose={(event, reason) => {
                if (reason === 'backdropClick') return;
                setShowPopup(false);
            }} maxWidth="sm">
                <DialogContent>
                    <TransportMaster mode="add" onPopupCancel={handlePopupClose} connectionString={connectionString} />
                </DialogContent>
            </Dialog>

            <Snackbar open={snackbar.open} autoHideDuration={2000} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} >
                <Alert onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} severity={snackbar.severity} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Paper>
    );
};

export default TransporterDetails;